import { defineStore } from 'pinia'
import { Ref, computed, ref } from 'vue'

import api from '@api/index'

import { BookmarkType } from '@models/bookmark/BookmarkFactory'
import { ExtentBookmark } from '@models/bookmark/ExtentBookmark'
import { PointBookmark } from '@models/bookmark/PointBookmark'
import { showToastSuccess } from '@services/ToastService'

import i18n from 'src/i18n'
import { useLastFetched } from 'src/composables/lastFetched'

export const useBookmarkStore = defineStore('bookmarkStore', () => {
  const { updateLastFetched, hasTimePassed } = useLastFetched(60)

  const allBookmarks = ref<BookmarkType[]>([]) as Ref<BookmarkType[]>

  const FETCH_BOOKMARKS = async () => {
    try {
      if (hasTimePassed()) {
        updateLastFetched()
        allBookmarks.value = await api.bookmarks.getUserBookmarks()
      }
    } catch (e) {
      console.error(e)
    }
  }

  const clearBookmarks = () => {
    allBookmarks.value = []
  }

  const pointBookmarks = computed(() => {
    return allBookmarks.value
      .filter((x) => x.isPoint())
      .map((x) => x as PointBookmark)
  })

  const extentBookmarks = computed(() => {
    return allBookmarks.value
      .filter((x) => x.isExtent())
      .map((x) => x as ExtentBookmark)
  })

  const organizationBookmarks = computed(() => {
    return allBookmarks.value
      .filter((x) => x.isFromOrganization)
      .map((x) => x as ExtentBookmark)
  })

  const isBookmarkNameAvailable = (name: string) => {
    const pointBookmark = pointBookmarks.value.find((x) => x.name === name)
    const extentBookmark = extentBookmarks.value.find((x) => x.name === name)
    return !pointBookmark && !extentBookmark
  }

  const ADD_BOOKMARK = async (bookmark: BookmarkType) => {
    try {
      await api.bookmarks.putBookmark(bookmark)
      allBookmarks.value.push(bookmark)
      showToastSuccess(i18n.t('bookmarkAdded'), i18n.t('bookmarkAddedSuccess'))
    } catch (error) {
      console.error(error)
    }
  }

  const DELETE_BOOKMARK = async (bookmark: BookmarkType) => {
    try {
      await api.bookmarks.deleteBookmark(bookmark)
      allBookmarks.value = allBookmarks.value.filter(
        (x) => x.id !== bookmark.id
      )
    } catch (error) {
      console.error(error)
    }
  }

  return {
    pointBookmarks,
    extentBookmarks,
    organizationBookmarks,

    isBookmarkNameAvailable,
    clearBookmarks,

    FETCH_BOOKMARKS,
    ADD_BOOKMARK,
    DELETE_BOOKMARK
  }
})
